import React, { useState, useEffect } from 'react';
import {
  Modal,
  Card,
  Row,
  Col,
  Typography,
  Space,
  Tag,
  Button,
  Image,
  Timeline,
  message,
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  adminService,
  AdminListingDetails as AdminListingDetailsType,
} from '../../api/services/adminService';
import {
  CarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { getFeatureLabel } from '../../utils/featureMapper';

const { Title, Text } = Typography;

interface AdminListingDetailsProps {
  visible: boolean;
  listing: any;
  onClose: () => void;
}

export const AdminListingDetails: React.FC<AdminListingDetailsProps> = ({
  visible,
  listing,
  onClose,
}) => {
  const { t } = useTranslation();
  const [listingDetails, setListingDetails] =
    useState<AdminListingDetailsType | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible && listing?.id) {
      fetchListingDetails();
    }
  }, [visible, listing?.id]);

  const fetchListingDetails = async () => {
    if (!listing?.id) return;

    setLoading(true);
    try {
      const details = await adminService.getListingDetails(listing.id);
      setListingDetails(details);
    } catch (error) {
      console.error('Failed to fetch listing details:', error);
      message.error(t('admin.listing_details.msg_load_failed'));
    } finally {
      setLoading(false);
    }
  };

  if (!listing) return null;

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
      token_verification_pending: { color: 'orange', text: t('admin.listing_details.status_token_review'), icon: <ClockCircleOutlined /> },
      under_approval: { color: 'purple', text: t('admin.listing_details.status_under_approval'), icon: <CheckCircleOutlined /> },
      token_verified: { color: 'green', text: t('admin.listing_details.status_token_verified'), icon: <CheckCircleOutlined /> },
      token_rejected: { color: 'red', text: t('admin.listing_details.status_token_rejected'), icon: <ExclamationCircleOutlined /> },
      active: { color: 'green', text: t('admin.listing_details.status_active'), icon: <CheckCircleOutlined /> },
      rejected: { color: 'red', text: t('admin.listing_details.status_rejected'), icon: <ExclamationCircleOutlined /> },
    };
    const config = statusConfig[status] || statusConfig.token_verification_pending;
    return <Tag color={config.color} icon={config.icon}>{config.text}</Tag>;
  };

  const getTimelineItems = () => {
    if (!listingDetails?.timeline) {
      return [
        {
          color: 'green',
          children: (
            <div>
              <Text strong>{t('admin.listing_details.listing_created')}</Text>
              <br />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {new Date(listing.submittedAt || listing.createdAt).toLocaleString()}
              </Text>
            </div>
          ),
        },
      ];
    }

    return listingDetails.timeline.map((item, index) => ({
      color: index === 0 ? 'green' : 'blue',
      children: (
        <div>
          <Text strong>{item.description}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {new Date(item.timestamp).toLocaleString()}
          </Text>
        </div>
      ),
    }));
  };

  return (
    <Modal
      title={
        <Space>
          <CarOutlined />
          <span>{t('admin.listing_details.title')}</span>
          {getStatusTag(listingDetails?.status || listing.status)}
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t('admin.listing_details.btn_close')}
        </Button>,
      ]}
      width={1000}
      style={{ top: 20 }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Vehicle Information */}
        <Card title={t('admin.listing_details.section_vehicle')} size="small">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <div style={{ textAlign: 'center' }}>
                <Image
                  width={200}
                  height={150}
                  src={
                    (listingDetails?.images?.[0] as any)?.url ||
                    (typeof listingDetails?.images?.[0] === 'string' ? listingDetails.images[0] : null) ||
                    (listing.images?.[0] as any)?.url ||
                    (typeof listing.images?.[0] === 'string' ? listing.images[0] : null) ||
                    '/images/default-car.jpg'
                  }
                  fallback="/images/default-car.jpg"
                  style={{ objectFit: 'cover', borderRadius: 8 }}
                />
              </div>
            </Col>
            <Col xs={24} md={16}>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Title level={4} style={{ margin: 0 }}>
                  {listingDetails?.title || listing.title}
                </Title>
                <Row gutter={[16, 8]}>
                  <Col span={12}>
                    <Text strong>{t('admin.listing_details.label_year')}</Text>
                    <br />
                    <Text>{listingDetails?.year || listing.year}</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>{t('admin.listing_details.label_mileage')}</Text>
                    <br />
                    <Text>{(listingDetails?.mileage || listing.mileage)?.toLocaleString()} km</Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>{t('admin.listing_details.label_price')}</Text>
                    <br />
                    <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                      {(listingDetails?.price || listing.price)?.toLocaleString()} ALL
                    </Text>
                  </Col>
                  <Col span={12}>
                    <Text strong>{t('admin.listing_details.label_location')}</Text>
                    <br />
                    <Text>
                      <EnvironmentOutlined />{' '}
                      {listingDetails?.location || listing.location || t('admin.listing_details.not_specified')}
                    </Text>
                  </Col>
                </Row>
                {(listingDetails?.description || listing.description) && (
                  <div>
                    <Text strong>{t('admin.listing_details.label_description')}</Text>
                    <br />
                    <Text>{listingDetails?.description || listing.description}</Text>
                  </div>
                )}
                {((listingDetails?.features && listingDetails.features.length > 0) ||
                  (listing.features && listing.features.length > 0)) && (
                  <div>
                    <Text strong>{t('admin.listing_details.label_features')}</Text>
                    <br />
                    <Space wrap>
                      {(listingDetails?.features || listing.features || []).map(
                        (feature: string, index: number) => (
                          <Tag key={index}>{getFeatureLabel(feature)}</Tag>
                        )
                      )}
                    </Space>
                  </div>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* User Information */}
        <Card title={t('admin.listing_details.section_user')} size="small">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Space direction="vertical" size="small">
                <div>
                  <UserOutlined style={{ marginRight: 8 }} />
                  <Text strong>{listingDetails?.user?.name || listing.user?.name}</Text>
                </div>
                <div>
                  <MailOutlined style={{ marginRight: 8 }} />
                  <Text>{listingDetails?.user?.email || listing.user?.email}</Text>
                </div>
                <div>
                  <PhoneOutlined style={{ marginRight: 8 }} />
                  <Text>{listingDetails?.user?.phone || listing.user?.phone || t('admin.listing_details.not_provided')}</Text>
                </div>
              </Space>
            </Col>
            <Col xs={24} md={12}></Col>
          </Row>
        </Card>

        {/* Status Timeline */}
        <Card title={t('admin.listing_details.section_status')} size="small">
          <Timeline items={getTimelineItems()} />
        </Card>

        {/* Quick Actions */}
        <Card title={t('admin.listing_details.section_quick_actions')} size="small">
          <Space wrap>
            <Button
              icon={<CarOutlined />}
              onClick={() => {
                const listingId = listingDetails?.id || listing.id;
                if (listingId) {
                  window.open(`/listing/${listingId}`, '_blank');
                }
              }}
            >
              {t('admin.listing_details.btn_view_full')}
            </Button>
          </Space>
        </Card>
      </Space>
    </Modal>
  );
};
